/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import { Control, FieldError, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Literal, MaterialProperties } from '@steelbuy/ts-shared';
import { SortComparator } from '@steelbuy/util';
import { FormControl } from '../primitive/form-control/FormControl';
import { NumberInput } from '../primitive/number-input/NumberInput';
import { Select } from '../primitive/select/Select';
import { TextInput } from '../primitive/text/TextInput';

export const OTHER = 'OTHER';

export interface MaterialSelectionProps<TFieldValues extends FieldValues = FieldValues> {
    material: MaterialProperties;
    name: any;
    control: Control<TFieldValues>;
    rules: any;
    otherInputName?: any;
    error?: FieldError;
    otherError?: FieldError;
    getOptions: (material: MaterialProperties) => any;
    label?: string;
    allowOtherInput?: boolean;
    otherInputType?: 'text' | 'number';
    otherRules?: any;
    labelMapper?: (option: any) => string;
    sort?: SortComparator;
    isRequired?: boolean;
}

export const MaterialSelection = <TFieldValues extends FieldValues = FieldValues>({
    material,
    name,
    control,
    rules,
    error,
    getOptions,
    label,
    allowOtherInput,
    otherInputName,
    otherError,
    otherInputType,
    otherRules,
    labelMapper,
    sort,
    isRequired = true,
}: MaterialSelectionProps<TFieldValues>) => {
    const { t } = useTranslation(['uiDomain', 'domainModel']);
    const { watch, unregister, trigger, setValue } = useFormContext();
    const options = getOptions(material).slice().sort(sort);

    const selectOptions = options.map((option: any) => ({
        label: labelMapper?.(option) ?? t(`domainModel:material.${name}.value.${option}` as Literal),
        value: option,
    }));

    useEffect(() => {
        if (options.length) {
            if (watch(name) !== undefined && !options.includes(watch(name))) {
                setValue(name, undefined);
            }
            trigger(name);
        } else {
            unregister(name);
        }
    }, [JSON.stringify(options), trigger, name, unregister, watch]);

    useEffect(() => {
        if (allowOtherInput && watch(name) === OTHER) {
            trigger(otherInputName);
        }
    }, [watch(name), allowOtherInput, trigger, otherInputName]);

    useEffect(() => {
        const value = watch(name);
        if (allowOtherInput) {
            if (value !== undefined && value !== OTHER && !options.includes(value)) {
                setValue(name, OTHER);
                setValue(otherInputName, value);
            }
        }
    }, []);

    useEffect(() => {
        if (options.length === 1) {
            const singleValue = options[0];

            if (watch(name) !== singleValue) {
                setValue(name, singleValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        }
    }, [options.length, options[0]]);

    if (options.length === 0) {
        return null;
    }

    return (
        <>
            <FormControl label={label} isMandatory={isRequired}>
                <Select
                    options={selectOptions}
                    placeholder={t(`uiDomain:materialInput.placeholder`)}
                    name={name}
                    control={control}
                    rules={rules}
                    error={error?.message}
                    disabled={options.length === 1}
                />
            </FormControl>
            {allowOtherInput && watch(name) === OTHER && (
                <FormControl label={t('uiDomain:materialSelection.otherLabel')} isMandatory>
                    {otherInputType === 'number' ? (
                        <NumberInput
                            control={control}
                            name={otherInputName}
                            rules={otherRules}
                            error={otherError?.message}
                        />
                    ) : (
                        <TextInput
                            control={control}
                            name={otherInputName}
                            rules={otherRules}
                            error={otherError?.message}
                        />
                    )}
                </FormControl>
            )}
        </>
    );
};
